import { Quill } from 'react-quill'

const Block = Quill.import('blots/block')
const Container = Quill.import('blots/container')
const Module = Quill.import('core/module')
const Parchment = Quill.import('parchment')
const Delta = Quill.import('delta')

function tableId() {
  return `row-${Math.random().toString(36).slice(2, 8)}`
}

function childAt(parent, index) {
  let i = 0
  let child = parent.children.head
  while (child) {
    if (i === index) return child
    i += 1
    child = child.next
  }
  return null
}

function childIndex(parent, blot) {
  let i = 0
  let child = parent.children.head
  while (child) {
    if (child === blot) return i
    i += 1
    child = child.next
  }
  return -1
}

function cellRowId(cell) {
  return cell && cell.domNode ? TableCell.formats(cell.domNode) : ''
}

function rowIdOf(row) {
  return cellRowId(row && row.children && row.children.head)
}

class TableCell extends Block {
  static create(value) {
    const node = super.create()
    node.setAttribute('data-row', value || tableId())
    return node
  }

  static formats(domNode) {
    return domNode.getAttribute('data-row') || undefined
  }

  cellOffset() {
    return this.parent ? childIndex(this.parent, this) : -1
  }

  format(name, value) {
    if (name === TableCell.blotName && value) {
      this.domNode.setAttribute('data-row', value)
      return
    }
    super.format(name, value)
  }

  row() {
    return this.parent
  }

  rowOffset() {
    return this.row() ? childIndex(this.row().parent, this.row()) : -1
  }

  table() {
    return this.row() && this.row().parent && this.row().parent.parent
  }

  optimize(context) {
    super.optimize(context)
    if (this.parent && this.parent.statics.blotName !== TableRow.blotName) {
      this.wrap(TableRow.blotName)
    }
  }
}
TableCell.blotName = 'table'
TableCell.tagName = ['TD', 'TH']

class TableRow extends Container {
  optimize(context) {
    super.optimize(context)
    this.children.forEach((child) => {
      if (child.next == null) return
      if (cellRowId(child) !== cellRowId(child.next)) {
        const after = this.split(child.offset(this) + child.length())
        if (after && after.optimize) after.optimize(context)
        if (this.prev && this.prev.optimize) this.prev.optimize(context)
      }
    })
    if (
      this.next &&
      this.next.statics &&
      this.next.statics.blotName === TableRow.blotName &&
      rowIdOf(this) &&
      rowIdOf(this) === rowIdOf(this.next)
    ) {
      this.next.moveChildren(this)
      this.next.remove()
    }
    if (this.parent && this.parent.statics.blotName !== TableBody.blotName) {
      this.wrap(TableBody.blotName)
    }
  }

  rowOffset() {
    return this.parent ? childIndex(this.parent, this) : -1
  }

  table() {
    return this.parent && this.parent.parent
  }
}
TableRow.blotName = 'table-row'
TableRow.tagName = 'TR'
TableRow.scope = Parchment.Scope.BLOCK_BLOT

class TableBody extends Container {
  optimize(context) {
    super.optimize(context)
    if (
      this.next &&
      this.next.statics &&
      this.next.statics.blotName === TableBody.blotName
    ) {
      this.next.moveChildren(this)
      this.next.remove()
    }
    if (this.parent && this.parent.statics.blotName !== TableContainer.blotName) {
      this.wrap(TableContainer.blotName)
    }
  }
}
TableBody.blotName = 'table-body'
TableBody.tagName = 'TBODY'
TableBody.scope = Parchment.Scope.BLOCK_BLOT

class TableContainer extends Container {
  balanceCells() {
    const rows = this.descendants(TableRow)
    const maxColumns = rows.reduce((max, row) => Math.max(row.children.length, max), 0)
    rows.forEach((row) => {
      const missing = maxColumns - row.children.length
      for (let i = 0; i < missing; i += 1) {
        const value = row.children.head ? TableCell.formats(row.children.head.domNode) : tableId()
        const blot = Parchment.create(TableCell.blotName, value)
        row.appendChild(blot)
        if (blot.optimize) blot.optimize({})
      }
    })
  }

  deleteColumn(index) {
    const body = this.children.head
    if (!body || !body.children.head) return
    body.children.forEach((row) => {
      const cell = childAt(row, index)
      if (cell) cell.remove()
    })
  }

  insertColumn(index) {
    const body = this.children.head
    if (!body || !body.children.head) return
    body.children.forEach((row) => {
      const ref = childAt(row, index)
      const value = TableCell.formats(row.children.head.domNode)
      const cell = Parchment.create(TableCell.blotName, value)
      row.insertBefore(cell, ref)
    })
  }

  insertRow(index) {
    const body = this.children.head
    if (!body || !body.children.head) return
    const id = tableId()
    const row = Parchment.create(TableRow.blotName)
    let col = body.children.head.children.head
    while (col) {
      row.appendChild(Parchment.create(TableCell.blotName, id))
      col = col.next
    }
    const ref = childAt(body, index)
    body.insertBefore(row, ref)
  }
}
TableContainer.blotName = 'table-container'
TableContainer.tagName = 'TABLE'
TableContainer.scope = Parchment.Scope.BLOCK_BLOT
TableContainer.className = 'rte-table'

TableContainer.allowedChildren = [TableBody]
TableBody.allowedChildren = [TableRow]
TableRow.allowedChildren = [TableCell]
TableContainer.defaultChild = 'table-body'
TableBody.defaultChild = 'table-row'
TableRow.defaultChild = 'table'

function deltaEndsWithNewline(delta) {
  if (!delta || !delta.ops || !delta.ops.length) return false
  for (let i = delta.ops.length - 1; i >= 0; i -= 1) {
    const op = delta.ops[i]
    if (typeof op.insert !== 'string') return false
    if (op.insert.length) return op.insert.endsWith('\n')
  }
  return false
}

function matchTableCell(node, delta) {
  const tr = node.parentNode
  let rowId = node.getAttribute('data-row')
  if (!rowId && tr && tr.nodeName === 'TR') {
    rowId = tr.getAttribute('data-row')
    if (!rowId) {
      rowId = tableId()
      tr.setAttribute('data-row', rowId)
    }
  }
  rowId = rowId || tableId()
  let next = delta
  if (!deltaEndsWithNewline(next)) {
    next = next.insert('\n')
  }
  return next.reduce((memo, op) => {
    const attrs = Object.assign({}, op.attributes || {}, { table: rowId })
    return memo.push(Object.assign({}, op, { attributes: attrs }))
  }, new Delta())
}

class TableModule extends Module {
  static register() {
    Quill.register(TableCell, true)
    Quill.register(TableRow, true)
    Quill.register(TableBody, true)
    Quill.register(TableContainer, true)
  }

  constructor(quill, options) {
    super(quill, options)
    this.quill.clipboard.addMatcher('td', matchTableCell)
    this.quill.clipboard.addMatcher('th', matchTableCell)
    this.quill.on(Quill.events.SCROLL_OPTIMIZE, (mutations) => {
      if (!Array.isArray(mutations)) return
      const hit = mutations.some((mutation) => {
        const tag = mutation.target && mutation.target.tagName
        return tag && ['TD', 'TH', 'TR', 'TBODY', 'TABLE'].indexOf(tag) !== -1
      })
      if (!hit) return
      this.quill.once(Quill.events.TEXT_CHANGE, (delta, oldDelta, source) => {
        if (source === Quill.sources.USER) this.balanceTables()
      })
    })
  }

  balanceTables() {
    this.quill.scroll.descendants(TableContainer).forEach((table) => {
      table.balanceCells()
    })
  }

  getTable(range) {
    const sel = range || this.quill.getSelection()
    if (sel == null) return [null, null, null]
    const [cell] = this.quill.getLine(sel.index)
    if (!cell || cell.statics.blotName !== TableCell.blotName) {
      return [null, null, null]
    }
    const row = cell.parent
    const table = row && row.parent && row.parent.parent
    if (!table || table.statics.blotName !== TableContainer.blotName) {
      return [null, null, null]
    }
    return [table, row, cell]
  }

  insertTable(rows, columns) {
    let range = this.quill.getSelection(true)
    if (range == null) {
      range = { index: Math.max(this.quill.getLength() - 1, 0), length: 0 }
    }
    const [existing] = this.getTable(range)
    if (existing) {
      range = {
        index: existing.offset(this.quill.scroll) + existing.length(),
        length: 0,
      }
    }
    let delta = new Delta().retain(range.index)
    if (range.length) delta = delta.delete(range.length)
    for (let r = 0; r < rows; r += 1) {
      const id = tableId()
      for (let c = 0; c < columns; c += 1) {
        delta = delta.insert('\n', { table: id })
      }
    }
    delta = delta.insert('\n')
    this.quill.updateContents(delta, Quill.sources.USER)
    this.quill.setSelection(range.index, Quill.sources.SILENT)
    this.balanceTables()
  }

  insertRowBelow() {
    const range = this.quill.getSelection()
    const [table, row] = this.getTable(range)
    if (!table || !row) return
    table.insertRow(row.rowOffset() + 1)
    this.quill.update(Quill.sources.USER)
  }

  insertColumnRight() {
    const range = this.quill.getSelection()
    const [table, , cell] = this.getTable(range)
    if (!table || !cell) return
    table.insertColumn(cell.cellOffset() + 1)
    this.quill.update(Quill.sources.USER)
  }

  deleteRow() {
    const [, row] = this.getTable()
    if (!row) return
    row.remove()
    this.quill.update(Quill.sources.USER)
  }

  deleteColumn() {
    const [table, , cell] = this.getTable()
    if (!cell) return
    table.deleteColumn(cell.cellOffset())
    this.quill.update(Quill.sources.USER)
  }

  deleteTable() {
    const [table] = this.getTable()
    if (!table) return
    const offset = table.offset(this.quill.scroll)
    table.remove()
    this.quill.update(Quill.sources.USER)
    this.quill.setSelection(offset, Quill.sources.SILENT)
  }
}

function registerQuillTable() {
  if (registerQuillTable.done) return
  Quill.register('modules/table', TableModule, true)
  registerQuillTable.done = true
}

export { registerQuillTable, TableModule, tableId }
